'use client'

import {
  IconKakaoLogo,
  IconLink,
  IconMoreHorizontal,
} from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'

import { OptimizedImage } from '@/shared/ui'

import { useModoocarShare } from '../../_hooks'
import shareIllust from '../_assets/share-illust.png'
import {
  RANK_TRACK_CODE,
  useRankImpression,
  useRankTrackingContext,
} from '../_features/rank'

const SHARE_BUBBLE = '내 주변에 공유해보세요'
const KAKAO_DESCRIPTION = '마이클 오너 실측 데이터로 보는 차종 랭킹'

/** 카카오 브랜드 옐로 / 더보기 버튼 배경 / 말풍선 배경 (DS 토큰 없음) */
const KAKAO_YELLOW = '#FEE500'
const MORE_GRAY = '#8E98A0'
const BUBBLE_BG = '#F2F4F6'

interface IRankShareCardProps {
  /** 공유 카드 제목 (예: '국내 소형 오너 만족도 랭킹') */
  title: string
  /** 카카오 공유 썸네일 (절대 URL) */
  imageUrl: string
  /** 인앱 웹뷰 여부 — 더보기를 앱 네이티브 공유 시트로 분기 */
  isInApp?: boolean
}

/**
 * 공유 카드 — Figma `+공유` 8624:163893
 * 공유 동작은 상세와 동일한 useModoocarShare로 처리한다
 * (카카오 sendCustom / 링크 복사 execCommand 폴백 / 더보기 인앱 네이티브 시트 분기).
 */
const RankShareCard = ({
  title,
  imageUrl,
  isInApp = false,
}: IRankShareCardProps) => {
  const { handleKakaoShare, handleCopyLink, handleShareMore } =
    useModoocarShare({
      isInApp,
      getShareMeta: () => ({
        url: typeof window === 'undefined' ? '' : window.location.href,
        title,
        description: KAKAO_DESCRIPTION,
        imageUrl,
      }),
    })

  const track = useRankTrackingContext()
  const shareRef = useRankImpression<HTMLElement>(() =>
    track.sectionShow(RANK_TRACK_CODE.SHARE, '공유하기')
  )
  const onKakao = () => {
    track.itemClick(RANK_TRACK_CODE.SHARE, 'button', '카카오 공유')
    handleKakaoShare()
  }
  const onCopy = () => {
    track.itemClick(RANK_TRACK_CODE.SHARE, 'button', '링크 복사 공유')
    handleCopyLink()
  }
  const onMore = () => {
    track.itemClick(RANK_TRACK_CODE.SHARE, 'button', '더보기 공유')
    handleShareMore()
  }

  return (
    <section
      ref={shareRef}
      className='flex flex-col items-center gap-dimension-x6 bg-background-surface-default px-dimension-x5 pt-dimension-x10 lg:overflow-hidden lg:rounded-dimension-x5'
    >
      <div className='flex gap-dimension-x4'>
        <button
          type='button'
          onClick={onKakao}
          aria-label='카카오톡으로 공유'
          className='flex h-14 w-14 items-center justify-center rounded-full'
          style={{ backgroundColor: KAKAO_YELLOW, color: '#000000' }}
        >
          <IconKakaoLogo width={32} height={32} />
        </button>
        <button
          type='button'
          onClick={onCopy}
          aria-label='링크 복사'
          className='flex h-14 w-14 items-center justify-center rounded-full bg-background-brand-strong text-foreground-static-white'
        >
          <IconLink width={26} height={26} />
        </button>
        <button
          type='button'
          onClick={onMore}
          aria-label='더보기'
          className='flex h-14 w-14 items-center justify-center rounded-full text-foreground-static-white lg:hidden'
          style={{ backgroundColor: MORE_GRAY }}
        >
          <IconMoreHorizontal width={28} height={28} />
        </button>
      </div>

      <div className='flex flex-col items-center'>
        <div
          className='rounded-full px-dimension-x6 py-dimension-x3'
          style={{ backgroundColor: BUBBLE_BG }}
        >
          <Text variant='title-2-bold' color='text-foreground-neutral-strong'>
            {SHARE_BUBBLE}
          </Text>
        </div>
        <span
          aria-hidden
          className='h-0 w-0 border-x-[16px] border-t-[20px] border-x-transparent'
          style={{ borderTopColor: BUBBLE_BG }}
        />
        <OptimizedImage
          src={shareIllust}
          alt=''
          width={120}
          height={102}
          radius={0}
          sizes='120px'
          className='mt-dimension-x2'
        />
      </div>
    </section>
  )
}

export default RankShareCard
